/**
 * Better Auth Utilities
 * Direct usage of Better Auth client following official patterns
 * Reference: https://www.better-auth.com/docs/basic-usage
 */

import { authClient, getSession } from './auth-client'

export interface AuthUser {
  id: string
  email: string
  name?: string
  emailVerified: boolean
  createdAt: string
  updatedAt: string
}

export interface AuthResponse {
  authenticated: boolean
  user?: AuthUser
  message?: string
}

/**
 * Check authentication status using Better Auth client
 */
export async function checkAuth(): Promise<AuthResponse> {
  try {
    const { data: session, error } = await getSession()
    
    if (error) {
      console.warn('Auth check error:', error)
      return { authenticated: false }
    }
    
    if (session && session.user) {
      return {
        authenticated: true,
        user: {
          id: session.user.id,
          email: session.user.email,
          name: session.user.name,
          emailVerified: session.user.emailVerified,
          createdAt: session.user.createdAt?.toISOString() || new Date().toISOString(),
          updatedAt: session.user.updatedAt?.toISOString() || new Date().toISOString()
        }
      }
    }
    
    return { authenticated: false }
  } catch (error) {
    console.error('Auth check failed:', error)
    return { authenticated: false }
  }
}

/**
 * Sign in with email and password using Better Auth
 */
export async function signIn(email: string, password: string): Promise<AuthResponse> {
  try {
    const { data, error } = await authClient.signIn.email({
      email,
      password,
      callbackURL: "/dashboard"
    })
    
    if (error) {
      throw new Error(error.message || 'Failed to sign in')
    }
    
    // Check if user is verified after successful sign-in
    if (data.user && !data.user.emailVerified) {
      // Sign out the user since they're not verified
      await signOut()
      throw new Error("Please verify your email address before signing in. Check your inbox for a verification email.")
    }
    
    return {
      authenticated: true,
      user: {
        id: data.user.id,
        email: data.user.email,
        name: data.user.name,
        emailVerified: data.user.emailVerified,
        createdAt: data.user.createdAt?.toISOString() || new Date().toISOString(),
        updatedAt: data.user.updatedAt?.toISOString() || new Date().toISOString()
      }
    }
  } catch (error) {
    console.error('Sign in error:', error)
    throw error
  }
}

/**
 * Sign up with email and password using Better Auth
 */
export async function signUp(email: string, password: string, name?: string): Promise<AuthResponse> {
  try {
    const { data, error } = await authClient.signUp.email({
      email,
      password,
      name: name || email.split('@')[0],
      callbackURL: "/dashboard"
    })
    
    if (error) {
      throw new Error(error.message || 'Failed to sign up')
    }
    
    // With email verification, user might not be immediately available
    if (data && data.user) {
      return {
        authenticated: true,
        user: {
          id: data.user.id,
          email: data.user.email,
          name: data.user.name,
          emailVerified: data.user.emailVerified,
          createdAt: data.user.createdAt?.toISOString() || new Date().toISOString(),
          updatedAt: data.user.updatedAt?.toISOString() || new Date().toISOString()
        }
      }
    }
    
    // User created but needs email verification
    return {
      authenticated: false,
      message: 'Account created successfully. Please check your email to verify your account.'
    }
  } catch (error) {
    console.error('Sign up error:', error)
    throw error
  }
}

/**
 * Sign out using Better Auth
 */
export async function signOut(): Promise<void> {
  try {
    await authClient.signOut({
      fetchOptions: {
        onSuccess: () => {
          // Clear any local state if needed
          window.location.href = "/login"
        }
      }
    })
  } catch (error) {
    console.error('Sign out error:', error)
    throw error
  }
}

/**
 * Get current session using Better Auth
 */
export async function getCurrentSession() {
  return getSession()
}

/**
 * Use session hook for reactive session management
 * This is the recommended way for client-side session access
 */
export const useSession = authClient.useSession

/**
 * Resend email verification
 */
export async function resendVerification(email: string): Promise<void> {
  try {
    const response = await fetch('https://auth.doc2plot.com/api/auth/resend-verification', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email })
    })
    
    if (!response.ok) {
      throw new Error(`Failed to resend verification: ${response.statusText}`)
    }
  } catch (error) {
    console.error('Resend verification error:', error)
    throw error
  }
}
